/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { NavCategory } from '@/lib/navigation/types';

/** SCO-109: regular links break into 1–5 columns. */
export const MAX_SUBNAV_COLUMNS = 5;

export type PlacedNavGroup = {
    group: NavCategory;
    /** Columns of the row the group occupies: 1 for Standard, the rest of the row for Across. */
    span: number;
    /** Children split column-major across `span`; a Standard group holds a single list. */
    columns: NavCategory[][];
};

export type NavGroupRow = {
    groups: PlacedNavGroup[];
};

export type LayoutNavGroupsOptions = {
    /** Columns the panel leaves to the category area, after the content component. */
    columns: number;
    /**
     * Large packs Standard groups side by side until the row fills; Small gives every
     * group its own row and only lets Across use the second column (SCO-NAV-05 Display Rules).
     */
    packStandardGroups?: boolean;
};

/** Even split, earlier columns taking the remainder — the ordering in SCO-NAV-05 Large D. */
function splitColumnMajor(items: NavCategory[], columnCount: number): NavCategory[][] {
    const count = Math.max(1, columnCount);
    const base = Math.floor(items.length / count);
    const extra = items.length % count;

    const columns: NavCategory[][] = [];
    let cursor = 0;
    for (let i = 0; i < count; i += 1) {
        const size = base + (i < extra ? 1 : 0);
        columns.push(items.slice(cursor, cursor + size));
        cursor += size;
    }
    return columns;
}

/**
 * Places grouping categories into subnav rows (SCO-NAV-05).
 *
 * A new row starts once the current one is full; the design has it begin below the
 * previous row's longest list, which is the grid's job (`items-start`), not this function's.
 */
export function layoutNavGroups(groups: NavCategory[], options: LayoutNavGroupsOptions): NavGroupRow[] {
    const columns = Math.min(MAX_SUBNAV_COLUMNS, Math.max(1, Math.trunc(options.columns)));
    const packStandardGroups = options.packStandardGroups ?? true;

    const rows: NavGroupRow[] = [];
    let current: PlacedNavGroup[] = [];
    let remaining = columns;

    const flush = () => {
        if (current.length > 0) {
            rows.push({ groups: current });
            current = [];
        }
        remaining = columns;
    };

    for (const group of groups) {
        // Across claims whatever the row has left, so anything after it starts a new row.
        const span = group.across ? remaining : 1;

        current.push({
            group,
            span,
            // The group still spans the full width, but never into empty columns: four values
            // over five columns is a spread, not four columns plus a hole.
            columns: group.across
                ? splitColumnMajor(group.children, Math.min(span, group.children.length))
                : [group.children],
        });
        remaining -= span;

        if (remaining <= 0 || !packStandardGroups) {
            flush();
        }
    }

    flush();
    return rows;
}
